import React from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { fadeInContainerWithStagger, fadeInUp } from '../../utils';
import { Heading, Text } from 'aplazame-design-system';
import Container from '../../componentsV2/Container';
import style from './style.module.scss';

function StepsSection(props) {
    const tl = React.useRef();

    const [activeTabIndex, setActiveTabIndex] = React.useState(0);

    const tabs = React.useRef();
    const tabsLinks = gsap.utils.selector(tabs);

    const tabContentWrapper = React.useRef();
    const tabContents = gsap.utils.selector(tabContentWrapper);

    const imageWrapperSteps = React.useRef();
    const imageSteps = gsap.utils.selector(imageWrapperSteps);

    React.useEffect(() => {
        gsap.timeline()
            .set(tabContents('div'), { display: 'none' })
            .set(tabContentWrapper.current.children[0], { display: 'block' })
            .to(imageSteps('img'), { autoAlpha: 0 })
            .to(imageWrapperSteps.current.children[0], { autoAlpha: 1 })
            .to(tabContentWrapper.current.children[0], { autoAlpha: 1 });
    }, []);

    const onTabClick = (index) => {
        // setActiveTabIndex(index);
        tl.current = gsap
            .timeline()
            .set(tabsLinks('li'), { color: '#231f20', fontWeight: 400 })
            .to(tabContents('div'), { duration: 0.2, autoAlpha: 0 })
            .set(tabContents('div'), { display: 'none', y: 40 })
            .to(tabs.current, {
                duration: 0.01,
                '--tab': `${index * 100}%`,
                ease: 'power2.out',
            })
            .set(tabs.current.children[index], {
                color: '#E7FFED',
                fontWeight: 500,
            })
            .to(imageSteps('img'), { autoAlpha: 0 })
            .set(tabContentWrapper.current.children[index], {
                display: 'block',
            })
            .to(tabContentWrapper.current.children[index], {
                autoAlpha: 1,
                y: 0,
            })
            .to(
                imageWrapperSteps.current.children[index],
                {
                    autoAlpha: 1,
                },
                '<'
            );
    };

    const handleTabListKeyDown = (e) => {
        if (document.activeElement === tabs.current) {

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    if (activeTabIndex < props.steps.length - 1) {
                        onTabClick(activeTabIndex + 1);
                        setActiveTabIndex(activeTabIndex + 1)
                    }
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    if (activeTabIndex > 0) {
                        onTabClick(activeTabIndex - 1);
                        setActiveTabIndex(activeTabIndex - 1)
                    }
                    break;
                case 'Home':
                    e.preventDefault();
                    onTabClick(0);
                    setActiveTabIndex(0)
                    break;
                case 'End':
                    e.preventDefault();
                    onTabClick(props.steps.length - 1);
                    setActiveTabIndex(props.steps.length - 1)
                    break;
                default:
                    break;
            }
        }
    };

    const onKeyDownEnter = (e, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onTabClick(index);
            setActiveTabIndex(index)
        }
    }

    return (
        <section
            aria-labelledby={props.id}
            // className={
            //     hasColorScheme ? colorStyle[`bg--${colorScheme}`] : ''
            // }
            className={style.bgcolor}
        >
            <Container>
                <motion.div
                    variants={fadeInContainerWithStagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ amount: 'some', once: true }}
                    className={style.wrapper}
                >
                    <motion.div
                        variants={fadeInUp}
                        className={style.wrapperImageSteps}
                    >
                        <img
                            className={style.wrapperImage}
                            src={props.backgroundImage.url}
                            alt={props.backgroundImage.alt}
                        />{' '}
                        <div ref={imageWrapperSteps}>
                            <img
                                style={{ zIndex: 3 }}
                                className={style.imageStep}
                                src={props.steps[0].image.url}
                                alt={props.steps[0].image.alt}
                            />
                            <img
                                style={{ zIndex: 2 }}
                                className={style.imageStep}
                                src={props.steps[1].image.url}
                                alt={props.steps[1].image.alt}
                            />
                            <img
                                style={{ zIndex: 1 }}
                                className={style.imageStep}
                                src={props.steps[2].image.url}
                                alt={props.steps[2].image.alt}
                            />
                        </div>
                    </motion.div>
                    <motion.div variants={fadeInUp}>
                        <div className={[style.wrapperHeading].join(' ')}>
                            <motion.div variants={fadeInUp}>
                                <Heading
                                    id={props.id}
                                    framer={1}
                                    tag={props.titleTag}
                                    className={[style.heading].join(' ')}
                                >
                                    {
                                        props.titleNode.childMarkdownRemark
                                            .htmlWithoutWrapper
                                    }
                                </Heading>
                            </motion.div>
                        </div>

                        <ul
                            tabIndex="0"
                            ref={tabs}
                            className={style.tabs}
                            onKeyDown={handleTabListKeyDown}
                            aria-label="Pasos"
                        >
                            <li
                                tabIndex="0"
                                role="button"
                                className={style.tabActive}
                                onClick={() => onTabClick(0)}
                                onKeyDown={(e) => onKeyDownEnter(e, 0)}
                            >
                                Paso 1
                            </li>
                            <li
                                role='button'
                                tabIndex="0"
                                onClick={() => onTabClick(1)}
                                onKeyDown={(e) => onKeyDownEnter(e, 1)}
                            >
                                Paso 2
                            </li>
                            <li
                                role='button'
                                tabIndex="0"
                                onClick={() => onTabClick(2)}
                                onKeyDown={(e) => onKeyDownEnter(e, 2)}
                            >
                                Paso 3
                            </li>
                        </ul>

                        <div
                            ref={tabContentWrapper}
                            className={style.wrapperTabContent}
                        >
                            {props.steps.map((step, index) => {
                                return (
                                    <div
                                        key={index}
                                        className={style.tabContent}
                                    >
                                        <Heading
                                            tag="h3"
                                            className={[
                                                style.headingTabContent,
                                            ].join(' ')}
                                            data-sal="slide-up"
                                            data-sal-easing="ease-out-quart"
                                        >
                                            {step.title}
                                        </Heading>
                                        <Text
                                            tag="p"
                                            className={[
                                                style.textTabContent,
                                            ].join(' ')}
                                            data-sal="slide-up"
                                            data-sal-easing="ease-out-quart"
                                        >
                                            {step.text}
                                        </Text>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                </motion.div>
            </Container>
        </section>
    );
}

export default StepsSection;
